import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { 
  ChipProps,
  ChipValueProps,
  DropDownListValueProps,
  setQueryKey,
  useNotification,
  useBaseHistory,
} from 'spindl-shared-components';

import { NOTIFICATION_DURATION } from '../../../lib/constants';
import { getTranslation } from '../../../lib/reactUtils';
import { displayUserInfo, downloadFile, formatDate, formatLastLogin, getQueryParams, isEmptyString } from '../../../lib/utils';
import { UserRowValueProps } from '../../molecules/UserRow';

import { Account, AccountStatus, ChipStyleEnum, SortableAccountColumns, SortOrders } from '../../../modules/users/types';
import { getAppConfig } from '../../../modules/users/users.repository';
import { ModalTypeEnum } from '../../pages/UserListPage/types';
import { getAllRoles } from '../../../modules/users/roles/roles.repository';

import { ChipColorPairs, UserAccount, UserAccountListCombinedProps, UserAccountListStateEnum } from './types';
import { deleteInvitationUseCase, exportAccountsUseCase, getUsersInvitationsUseCase, getUsersListUseCase, updateAccountUseCase } from './UserAccountList.interactor';
import { isUser, sortUserAccounts } from './utils';
import { useLocation } from 'react-router';

const usePresenter = (props: UserAccountListCombinedProps): UserAccountListCombinedProps => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const queryParams = getQueryParams(search);
  const page = queryParams.page ? Number(queryParams.page) : 1;
  const perPage = queryParams.perPage ? Number(queryParams.perPage) : 100;
  const history = useBaseHistory();
  const queryClient = useQueryClient();
  const {
    setShow,
    setSelectedAccount,
    setModalType,
    setChipProps,
    scrollContainerRef,
  } = props;

  const { trigger } = useNotification();

  const [submittedQuery, setSubmittedQuery] = useState<string>('');
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<SortableAccountColumns>('signupDate');
  const [sortOrder, setSortOrder] = useState<SortOrders>('desc');
  const [userAccounts, setUserAccounts] = useState<UserAccount[]>([]);
  const [blockState, setBlockState] = useState<UserAccountListStateEnum>('Empty');
  const [userRows, setUserRows] = useState<UserRowValueProps[]>([]);
  // accounts with roleId === null are matched with default role color and text
  const [chipColorPairs, setChipColorPairs] = useState<ChipColorPairs>({});

  const { data, isLoading } = useQuery(
    setQueryKey(['getUsers', page, perPage, submittedQuery, sortBy, sortOrder]),
    async () => getUsersListUseCase(page, perPage, submittedQuery, sortBy, sortOrder),
  );
  const { data: userInvitations } = useQuery(
    setQueryKey(['getInvitations', page, perPage, submittedQuery, sortBy, sortOrder]),
    async () => getUsersInvitationsUseCase(page, perPage, submittedQuery, sortBy, sortOrder),
  );
  const { data: appConfig } = useQuery(
    setQueryKey(['getAppConfig']),
    async () => getAppConfig(),
  );
  const { data: roleData } = useQuery(
    setQueryKey(['getAllRoles']),
    async () => getAllRoles(),
  );
  const { mutateAsync: exportAccounts, isLoading: loadingExportUser } = useMutation('exportAccounts', () => exportAccountsUseCase());
  const { mutateAsync: updateAccount } = useMutation('updateAccount', async (account?: UserAccount) => {
    if (account) {
      await updateAccountUseCase(account.id, { enabled: true });
    }
  });
  const { mutateAsync: deleteInvite } = useMutation('deleteInvite', async (account?: UserAccount) => {
    if (account) {
      await deleteInvitationUseCase(account.id);
    }
  });

  const mapChipProps = useCallback((roleId?: number): ChipProps & ChipValueProps => {
    if (roleId) {
      const foundRole = roleData?.find(role => role.id === roleId);
      return {
        styleType: chipColorPairs[roleId],
        text: {
          value: foundRole?.roleName,
        },
      };
    }
    return {
      text: {
        value: t('default.role'),
      },
    };
  }, [chipColorPairs, roleData, t]);

  useEffect(() => {
    if (appConfig && appConfig.verifyEmailEnabled) {
      setEmailVerificationEnabled(appConfig.verifyEmailEnabled.value);
    }
  }, [appConfig]);

  useEffect(() => {
    if (roleData) {
      const availableColors = Object.values(ChipStyleEnum).filter(color => {
        if (!Object.values(chipColorPairs).includes(ChipStyleEnum[color])) {
          return color;
        }
      });

      const unmatchedRoles = roleData.filter(role => {
        if (!Object.keys(chipColorPairs).includes(`${role.id}`)) {
          return role;
        }
      });

      const newChipColorPairs = chipColorPairs;
      for (let i = 0; i < unmatchedRoles.length; i ++) {
        newChipColorPairs[unmatchedRoles[i].id] = availableColors[i];
      }
      setChipColorPairs(newChipColorPairs);
    }
  }, [roleData, chipColorPairs]);

  useEffect(() => {
    // FIXME: Move the aggregate sorting of accounts and account invitations to the backend
    const accounts = sortUserAccounts(sortBy, sortOrder, data?.items || [], userInvitations?.items || [], roleData || []);
    setUserAccounts(accounts);

    if (accounts && accounts.length) {
      setBlockState('Default');
      return;
    } 

    if (!isEmptyString(submittedQuery)) {
      setBlockState('NoResults');
      return; 
    }

    setBlockState('Empty');
  }, [data?.items, sortBy, sortOrder, userInvitations, submittedQuery, roleData]);

  const handleSort = (sortingKey: SortableAccountColumns) => {
    if (sortingKey === sortBy) {
      setSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
      return;
    }

    const ALPHABETICAL_COLUMNS: SortableAccountColumns[] = ['name', 'email', 'role', 'status'];

    if (ALPHABETICAL_COLUMNS.includes(sortingKey)) {
      // alphabetical columns are sorted in ascending order for the first sorting action (A-Z)
      setSortOrder('asc');
    } else {
      // numerical columns are sorted in descending order for the first sorting action (newest to oldest)
      setSortOrder('desc');
    }

    setSortBy(sortingKey);
  };

  const whichSortingIcon = (sortingKey: SortableAccountColumns): 'Sort' | 'ArrowUp' | 'ArrowDown' => {
    if (sortingKey !== sortBy) {
      return 'Sort';
    }

    if (sortOrder === 'asc') {
      return 'ArrowUp';
    }

    return 'ArrowDown';
  };
    
  const handleSelectAccount = useCallback((account?: UserAccount) => {
    if (setSelectedAccount) {
      setSelectedAccount(account);
    }
    if (setChipProps) {
      setChipProps(mapChipProps(account?.roleId));
    }
  }, [mapChipProps, setChipProps, setSelectedAccount]);

  const handleInviteUsers = () => {
    history.push('/wizard');
  };

  const handleDropdownItemClicked = useCallback((modalType: ModalTypeEnum) => {
    if (setShow && setModalType) {
      setModalType(modalType);
      setShow(true);
    }
  }, [setModalType, setShow]);

  const handleDeleteUserInvite = useCallback(async (account?: UserAccount) => {
    try {
      await deleteInvite(account);
      trigger({
        duration: NOTIFICATION_DURATION,
        message: getTranslation('toast_notification.delete_invite', {
          email: account?.email,
        }),
        type: 'Positive',
      });
    } catch (e) {
      // no-op
    } finally {
      await queryClient.invalidateQueries('getInvitations');
    }
  }, [deleteInvite, queryClient, trigger]);

  const handleEnableUser = useCallback(async (account?: UserAccount) => {
    try {
      await updateAccount(account);
      trigger({
        duration: NOTIFICATION_DURATION,
        message: getTranslation('toast_notification.enable_user.success', {
          name: displayUserInfo(account),
        }),
        type: 'Positive',
      });
    } catch (e) {
      // no-op
    } finally {
      await queryClient.invalidateQueries('getUsers');
    }
  }, [queryClient, trigger, updateAccount]);

  const handleExportUsers = async () => {
    const exportedAccounts = await exportAccounts();
    downloadFile(exportedAccounts, t('default.export_user_file_name'), 'text/csv;charset=utf-8;');
  };

  const mapUserStatusProps = useCallback((status?: AccountStatus): any => {
    switch (status) {
      // Temporary set both copy for account status verified and activated to 'Activated' until further alignment
      case AccountStatus.verified:
      case AccountStatus.activated:
        return {
          targetProps: {
            text: {
              value: t('user_status.activated'),
            },
          },
        };
      case AccountStatus.invitation_sent:
      case AccountStatus.unverified:
        return {
          targetProps: {
            icon: {
              asset: 'Time',
              colour: 'IconWarning',
            },
            text: {
              value: t(`user_status.${status}`),
            },
          },
        };
      case AccountStatus.disabled:
        return {
          targetProps: {
            icon: {
              asset: 'NegativeFilled',
              colour: 'IconDestructive',
            },
            text: {
              value: t(`user_status.${status}`),
            },
          },
        };
    }
  }, [t]);

  const mapDropDownMenuProps = useCallback((status?: AccountStatus, account?: UserAccount): DropDownListValueProps => {
    switch (status) {
      case AccountStatus.verified:
      case AccountStatus.unverified:
      case AccountStatus.activated:
        return {
          dropDownItems: [
            {
              type: 'DefaultIconText',
              text: {
                value: t('button.disable_user'),
              },
              icon: {
                asset: 'NegativeOutline',
                colour: 'IconDark',
              },
              onClick: () => handleDropdownItemClicked(ModalTypeEnum.DisableUser),
            },
            {
              type: 'DefaultIconText',
              text: {
                value: t('button.reset_password'),
              },
              icon: {
                asset: 'Reset',
                colour: 'IconDark',
              },
              onClick: () => handleDropdownItemClicked(ModalTypeEnum.ResetPassword),
            },
            {
              type: 'DefaultIconText',
              text: {
                value: t('button.change_role'),
              },
              icon: {
                asset: 'UserOutline',
                colour: 'IconDark',
              },
              onClick: () => handleDropdownItemClicked(ModalTypeEnum.ChangeRole),
            },
            {
              type: 'DefaultIconText',
              text: {
                value: t('button.change_email'),
              },
              icon: {
                asset: 'Email',
                colour: 'IconDark',
              },
              onClick: () => handleDropdownItemClicked(ModalTypeEnum.EmailUpdate),
            },
          ],
        };
      case AccountStatus.invitation_sent:
        return {
          dropDownItems: [
            {
              type: 'DefaultIconText',
              text: {
                value: t('button.delete_invite'),
              },
              icon: {
                asset: 'Delete',
                colour: 'IconDark',
              },
              onClick: () => handleDeleteUserInvite(account),
            },
          ],
        };
      case AccountStatus.disabled:
        return {
          dropDownItems: [
            {
              type: 'DefaultIconText',
              text: {
                value: t('button.enable_user'),
              },
              icon: {
                asset: 'SelectedOutline',
                colour: 'IconDark',
              },
              onClick: () => handleEnableUser(account),
            },
          ],
        };
      default:
        return {
        };
    }
  }, [handleDeleteUserInvite, handleDropdownItemClicked, handleEnableUser, t]);

  const mapAccountStatus = useCallback((account: Account) => {
    if (!account.enabled) {
      return AccountStatus.disabled;
    }
    // For apps that don't require verifications, we don't have to consider 'verified' or 'unverified' so can be mapped to activated
    if (!emailVerificationEnabled) {
      return AccountStatus.activated;
    }
    return account.status;
  }, [emailVerificationEnabled]);

  // map user list item to props for each entry
  useEffect(() => {
    if (userAccounts && userAccounts.length) {
      setUserRows(userAccounts.map((user) => {
        const chipProps = mapChipProps(user.roleId);
        if (isUser(user)) {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`;
          const accountStatus = mapAccountStatus(user);
          return {
            name: {
              targetProps: {
                text: {
                  value: !isEmptyString(fullName) ? fullName.trim() : t('default.name'),
                },
              },
            },
            email: {
              targetProps: {
                text: {
                  value: user.email || t('default.email'),
                },
              },
            },
            role: {
              targetProps: {
                chip: chipProps,
              },
            },
            time: {
              targetProps: {
                text: {
                  value: user.lastLogin ? formatLastLogin(user.lastLogin, t) : t('default.last_login'),
                },
              },
            },
            date: {
              targetProps: {
                text: {
                  value: user.createdAt ? formatDate(user.createdAt) : t('default.signup_date'),
                },
              },
            },
            status: mapUserStatusProps(accountStatus),
            button: {
              targetProps: {
                iconDropDown: {
                  toggleProps: {
                    type: 'PillButton',
                    button: {
                      onClick: () => handleSelectAccount(user),
                    },
                  },
                  menuProps: mapDropDownMenuProps(accountStatus, user),
                },
              },
            },
          };
        } else {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`;
          return {
            name: {
              targetProps: {
                text: {
                  value: !isEmptyString(fullName) ? fullName.trim() : t('default.name'),
                },
              },
            },
            email: {
              targetProps: {
                text: {
                  value: user.email || t('default.email'),
                },
              },
            },
            role: {
              targetProps: {
                chip: chipProps,
              },
            },
            time: {
              targetProps: {
                text: {
                  value: t('default.last_login'),
                },
              },
            },
            date: {
              targetProps: {
                text: {
                  value: t('default.signup_date'),
                },
              },
            },
            // TODO: handle default state properly when account status update is implemented
            status: mapUserStatusProps(AccountStatus.invitation_sent),
            button: {
              targetProps: {
                iconDropDown: {
                  toggleProps: {
                    type: 'PillButton',
                    button: {
                    },
                  },
                  menuProps: mapDropDownMenuProps(AccountStatus.invitation_sent, user),
                },
              },
            },
          };
        }
      }));
    }
  }, [userAccounts, handleSelectAccount, mapAccountStatus, mapChipProps, mapDropDownMenuProps, mapUserStatusProps, t]);
  
  // return props
  return {
    ...props,
    state: isLoading ? 'Loading' : blockState,
    userListHeader: {
      title: {
        value: t('user_list.header.title'),
      },
      inviteButton: {
        text: {
          value: t('button.invite_users'),
        },
        onClick: handleInviteUsers,
      },
      exportButton: {
        text: {
          value: t('button.export'),
        },
        state: loadingExportUser ? 'Loading' : 'Default',
        icon: {
          asset: loadingExportUser ? 'Loading' : 'Export',
        },
        disabled: data?.totalItems === 0,
        onClick: handleExportUsers,
      },
      searchBar: {
        textPlaceholder: t('user_list.header.search'),
        icon: {
          asset: 'Search',
        },
        clearOnBlur: true,
        onSubmit: (query: string) => setSubmittedQuery(query.trim()),
      },
    },
    userPageBar: {
      page,
      perPage,
      totalItems: data?.totalItems,
      totalPages: data?.totalPages,
    },
    columnHeaders: {
      nameHeader: {
        targetProps: {
          icon: { asset: whichSortingIcon('name') },
          onClick: () => handleSort('name'),
        },
      },
      emailHeader: {
        targetProps: {
          icon: { asset: whichSortingIcon('email') },
          onClick: () => handleSort('email'),
        },
      },
      roleHeader: {
        targetProps: {
          icon: { asset: whichSortingIcon('role') },
          onClick: () => handleSort('role'),
        },
      },
      loginHeader: {
        targetProps: {
          icon: { asset: whichSortingIcon('lastLogin') },
          onClick: () => handleSort('lastLogin'),
        },
      },
      signupHeader: {
        targetProps: {
          icon: { asset: whichSortingIcon('signupDate') },
          onClick: () => handleSort('signupDate'),
        },
      },
      statusHeader: {
        targetProps: {
          icon: { asset: whichSortingIcon('status') },
          onClick: () => handleSort('status'),
        },
      },
    },
    userRowList: {
      userRows: userRows,
      scrollContainerRef,
    },
    title: {
      value: blockState === 'Empty' ? t('empty_content.empty.title') : t('empty_content.no_result.title'),
    },
    instructions: {
      value: blockState === 'Empty' ? t('empty_content.empty.instructions') : t('empty_content.no_result.instructions'),
    },
    button: {
      text: {
        value: t('button.invite_users'),
      },
    },
  };
};

export default usePresenter;
